import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import {
  X, ExternalLink, Code, Globe, Image, Download, FileText,
  FileSpreadsheet, FileType, File, Loader2, Folder, FolderOpen, ChevronRight, FolderTree,
  ArrowLeft, CornerLeftUp, RotateCw, Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { openHtmlInSandboxedTab, svgToImageSrc } from "@/lib/sandboxed-html";
import { ZoomableImage } from "@/components/chat/zoom-viewer";
import { MarkdownRenderer } from "./markdown-renderer";
import type { Artifact } from "./artifact-detector";
import { ChatToolbarTooltip } from "@/components/chat/chat-toolbar-tooltip";

// ── Artifact context ────────────────────────────────────────────────

type PanelMode = "artifact" | "browser";

interface ArtifactState {
  isOpen: boolean;
  mode: PanelMode;
  artifact: Artifact | null;
  /** Agent ID for file proxy requests */
  agentId: string | null;
  /** True when the current artifact was opened from the workspace-files browser,
      so the header control can return to that list instead of closing. */
  cameFromBrowser: boolean;
  openArtifact: (artifact: Artifact, agentId?: string) => void;
  openBrowser: (agentId: string) => void;
  /** Return from an artifact view to the workspace-files browser. */
  back: () => void;
  close: () => void;
}

const ArtifactContext = createContext<ArtifactState>({
  isOpen: false,
  mode: "artifact",
  artifact: null,
  agentId: null,
  cameFromBrowser: false,
  openArtifact: () => {},
  openBrowser: () => {},
  back: () => {},
  close: () => {},
});

export function ArtifactProvider({ children }: { children: ReactNode }) {
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [mode, setMode] = useState<PanelMode>("artifact");
  const [cameFromBrowser, setCameFromBrowser] = useState(false);

  return (
    <ArtifactContext.Provider
      value={{
        isOpen,
        mode,
        artifact,
        agentId,
        cameFromBrowser,
        openArtifact: (a, id) => {
          // Remember whether we drilled in from the files browser, so the
          // header control can offer "back to files" rather than closing.
          setCameFromBrowser(mode === "browser");
          setArtifact(a);
          setAgentId(id || null);
          setMode("artifact");
          setIsOpen(true);
        },
        openBrowser: (id) => {
          setCameFromBrowser(false);
          setAgentId(id);
          setMode("browser");
          setArtifact(null);
          setIsOpen(true);
        },
        back: () => {
          setMode("browser");
          setArtifact(null);
          setCameFromBrowser(false);
        },
        close: () => setIsOpen(false),
      }}
    >
      {children}
    </ArtifactContext.Provider>
  );
}

export function useArtifact() {
  return useContext(ArtifactContext);
}

// ── File type helpers ───────────────────────────────────────────────

const FILE_ICONS: Record<string, typeof FileText> = {
  pdf: FileText,
  csv: FileSpreadsheet,
  tsv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  docx: FileType,
  doc: FileType,
  pptx: FileType,
  ppt: FileType,
  md: FileText,
  txt: FileText,
  yaml: Code,
  yml: Code,
  json: Code,
  html: Globe,
  htm: Globe,
  xml: Code,
  png: Image,
  jpg: Image,
  jpeg: Image,
  gif: Image,
  svg: Image,
  webp: Image,
  bmp: Image,
  ico: Image,
  cab: File,
  zip: File,
  tar: File,
  gz: File,
};

const TEXT_EXTS = new Set(["md", "txt", "json", "xml", "csv", "tsv", "yaml", "yml"]);
const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp", "ico"]);
const SPREADSHEET_EXTS = new Set(["xlsx", "xls"]);
const HTML_EXTS = new Set(["html", "htm"]);
const DOCX_EXTS = new Set(["docx"]);

function getFileProxyUrl(agentId: string, filePath: string, download = false) {
  const params = new URLSearchParams({ path: filePath });
  if (download) params.set("download", "true");
  return `/api/agents/${agentId}/files?${params.toString()}`;
}

/** Path-shaped variant used by the HTML iframe preview. The path on
 *  the VM is encoded as URL segments so that relative URLs inside
 *  the rendered HTML (e.g. `<img src="foo.png">`) resolve to sibling
 *  files via the same proxy — which a `blob:` URL can't do. */
function getWorkspaceFileUrl(agentId: string, filePath: string) {
  const trimmed = filePath.replace(/^\/+/, "");
  const encoded = trimmed.split("/").map(encodeURIComponent).join("/");
  return `/api/agents/${agentId}/wf/${encoded}`;
}

// ── Preview components ──────────────────────────────────────────────

function ArtifactIcon({ type, fileExt }: { type: Artifact["type"]; fileExt?: string }) {
  if (type === "file" && fileExt) {
    const Icon = FILE_ICONS[fileExt] || File;
    return <Icon size={14} />;
  }
  switch (type) {
    case "html":
      return <Globe size={14} />;
    case "svg":
      return <Image size={14} />;
    case "code":
      return <Code size={14} />;
    default:
      return <FileText size={14} />;
  }
}

function HtmlPreview({ content }: { content: string }) {
  // Sandboxed, like the preview below. A plain blob: URL ran the agent's
  // scripts with ProClaw's origin and the user's session.
  const openInNewTab = () => openHtmlInSandboxedTab(content);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-end px-3 py-1.5 border-b border-border">
        <button
          onClick={openInNewTab}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
        >
          <ExternalLink size={12} />
          Open in new tab
        </button>
      </div>
      <iframe
        srcDoc={content}
        // allow-modals lets alert/confirm work (they were silently blocked);
        // still no allow-same-origin, so the page has no access to ProClaw.
        sandbox="allow-scripts allow-modals"
        className="flex-1 w-full bg-white rounded-b-lg"
        title="HTML Preview"
      />
    </div>
  );
}

function SvgPreview({ content }: { content: string }) {
  // Drawn as an <img>, so the SVG cannot run scripts or event handlers (it used
  // to be injected straight into the page). The wrapper scrolls, so a large
  // picture is no longer cut off.
  return (
    <div className="flex h-full items-center justify-center overflow-auto rounded-lg bg-white p-6 dark:bg-surface">
      <ZoomableImage src={svgToImageSrc(content)} alt="SVG preview" className="max-h-full max-w-full object-contain" />
    </div>
  );
}

function CodePreview({ content, language }: { content: string; language?: string }) {
  return (
    <div className="h-full overflow-auto">
      <pre className="p-4 text-sm font-mono leading-relaxed text-text-primary">
        <code>{content}</code>
      </pre>
    </div>
  );
}

/** File preview — fetches content from agent VM and renders appropriate viewer */
function FilePreview({ agentId, filePath, fileExt }: { agentId: string; filePath: string; fileExt: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);

  // HTML files are rendered via a path-shaped URL (see HTML_EXTS branch
  // below). Skip the fetch entirely so relative <img>/<link>/etc. inside
  // the HTML resolve to siblings via the same proxy.
  const isHtmlFile = HTML_EXTS.has(fileExt);

  useEffect(() => {
    let cancelled = false;
    let createdBlobUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextContent(null);
    setArrayBuffer(null);

    if (isHtmlFile) {
      // The <iframe> below does its own load; no client-side fetch needed.
      setLoading(false);
      return () => { cancelled = true; };
    }

    const url = getFileProxyUrl(agentId, filePath);
    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
        if (TEXT_EXTS.has(fileExt)) {
          const text = await res.text();
          if (!cancelled) setTextContent(text);
        } else if (SPREADSHEET_EXTS.has(fileExt) || DOCX_EXTS.has(fileExt)) {
          const buf = await res.arrayBuffer();
          if (!cancelled) setArrayBuffer(buf);
        } else {
          // PDF, images, and unknown binaries
          const blob = await res.blob();
          if (cancelled) return;
          createdBlobUrl = URL.createObjectURL(blob);
          setBlobUrl(createdBlobUrl);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load file");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [agentId, filePath, fileExt, isHtmlFile]);

  const downloadUrl = getFileProxyUrl(agentId, filePath, true);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <Loader2 size={24} className="animate-spin" />
        <p className="text-sm">Loading file from agent...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
        <FileText size={32} className="text-text-muted" />
        <p className="text-sm text-red-500">{error}</p>
        <a
          href={downloadUrl}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-xs font-medium hover:bg-accent/20 transition-colors"
        >
          <Download size={12} />
          Try downloading instead
        </a>
      </div>
    );
  }

  // PDF: use iframe
  if (fileExt === "pdf" && blobUrl) {
    return <iframe src={blobUrl} className="w-full h-full" title="PDF Preview" />;
  }

  // HTML file: iframe loads from the path-shaped /wf/ route directly
  // so that relative <img>/<link>/etc. inside the HTML resolve to
  // sibling files via the same proxy. (Inline ```html``` code blocks
  // arrive as a string and use HtmlPreview instead.)
  if (HTML_EXTS.has(fileExt)) {
    return (
      <iframe
        src={getWorkspaceFileUrl(agentId, filePath)}
        sandbox="allow-same-origin"
        className="w-full h-full bg-white"
        title="HTML Preview"
      />
    );
  }

  // Images: render inline
  if (IMAGE_EXTS.has(fileExt) && blobUrl) {
    return (
      <div className="flex items-center justify-center h-full p-4 bg-white dark:bg-surface overflow-auto">
        <ZoomableImage src={blobUrl} alt={filePath.split("/").pop() ?? "Image"} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  // Markdown: render with markdown renderer
  if (fileExt === "md" && textContent !== null) {
    return (
      <div className="h-full overflow-auto p-4">
        <MarkdownRenderer content={textContent} />
      </div>
    );
  }

  // CSV / TSV: render as table
  if ((fileExt === "csv" || fileExt === "tsv") && textContent !== null) {
    return <CsvTable content={textContent} delimiter={fileExt === "tsv" ? "\t" : ","} />;
  }

  // Excel: parse workbook client-side and render each sheet as a table
  if (SPREADSHEET_EXTS.has(fileExt) && arrayBuffer) {
    return <XlsxPreview buffer={arrayBuffer} />;
  }

  // DOCX: convert to HTML via mammoth.js (dynamic import keeps bundle slim)
  if (DOCX_EXTS.has(fileExt) && arrayBuffer) {
    return <DocxPreview buffer={arrayBuffer} />;
  }

  // JSON: render formatted
  if (fileExt === "json" && textContent !== null) {
    let formatted = textContent;
    try {
      formatted = JSON.stringify(JSON.parse(textContent), null, 2);
    } catch { /* use raw */ }
    return (
      <div className="h-full overflow-auto">
        <pre className="p-4 text-xs font-mono leading-relaxed text-text-primary whitespace-pre-wrap">{formatted}</pre>
      </div>
    );
  }

  // Other text: render as plain text
  if (textContent !== null) {
    return (
      <div className="h-full overflow-auto">
        <pre className="p-4 text-sm font-mono leading-relaxed text-text-primary whitespace-pre-wrap">{textContent}</pre>
      </div>
    );
  }

  // Binary files that can't be previewed (pptx, cab, zip, etc.) — offer download
  const filename = filePath.split("/").pop() || "file";
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
      {(() => {
        const Icon = FILE_ICONS[fileExt] || File;
        return <Icon size={48} className="text-text-muted" />;
      })()}
      <div className="text-center">
        <p className="text-sm font-medium text-text-primary">{filename}</p>
        <p className="text-xs text-text-muted mt-1">.{fileExt} file — preview not available</p>
      </div>
      <a
        href={downloadUrl}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
      >
        <Download size={14} />
        Download File
      </a>
    </div>
  );
}

/** Simple CSV / TSV table renderer */
function CsvTable({ content, delimiter = "," }: { content: string; delimiter?: string }) {
  const lines = content.trim().split("\n");
  const rows = lines.map((line) => {
    // Basic delimited parsing (handles quoted fields containing the delimiter)
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === delimiter && !inQuotes) {
        cells.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    return cells;
  });

  const header = rows[0] || [];
  const body = rows.slice(1);

  return (
    <div className="h-full overflow-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead className="bg-surface-hover sticky top-0">
          <tr>
            {header.map((cell, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-text-secondary border-b border-border whitespace-nowrap">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} className="hover:bg-surface-hover/50">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-1.5 text-text-primary border-b border-border/50 whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-3 py-2 text-[10px] text-text-muted">{body.length} rows</p>
    </div>
  );
}

/** Excel workbook renderer — parses xlsx/xls client-side via SheetJS
 *  and displays each sheet as a tabbable table. The xlsx library is
 *  dynamically imported so it doesn't bloat the main bundle. */
function XlsxPreview({ buffer }: { buffer: ArrayBuffer }) {
  const [sheets, setSheets] = useState<{ name: string; rows: unknown[][] }[] | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setActiveSheet(0);
    setError(null);

    import("xlsx")
      .then((XLSX) => {
        if (cancelled) return;
        try {
          const workbook = XLSX.read(buffer, { type: "array" });
          const parsed = workbook.SheetNames.map((name) => {
            const sheet = workbook.Sheets[name];
            // header: 1 gives us an array of arrays (raw rows)
            const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
              header: 1,
              defval: "",
              blankrows: false,
            });
            return { name, rows };
          });
          setSheets(parsed);
        } catch (err) {
          setError(err instanceof Error ? err.message : "Failed to parse workbook");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load xlsx library");
        }
      });

    return () => { cancelled = true; };
  }, [buffer]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 p-6">
        <FileSpreadsheet size={32} className="text-text-muted" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!sheets) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <Loader2 size={24} className="animate-spin" />
        <p className="text-sm">Parsing workbook...</p>
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-text-muted">Empty workbook</p>
      </div>
    );
  }

  const current = sheets[activeSheet];

  return (
    <div className="flex h-full flex-col">
      {/* Sheet tabs — only show if there are multiple sheets */}
      {sheets.length > 1 && (
        <div className="shrink-0 flex items-center gap-0 overflow-x-auto border-b border-border bg-surface/30">
          {sheets.map((s, i) => (
            <button
              key={s.name}
              onClick={() => setActiveSheet(i)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors border-r border-border/50",
                i === activeSheet
                  ? "bg-background text-text-primary border-b-2 border-b-accent"
                  : "text-text-muted hover:bg-surface-hover",
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {current.rows.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-text-muted">Empty sheet</p>
          </div>
        ) : (
          <table className="min-w-full text-xs border-collapse">
            <thead className="bg-surface-hover sticky top-0 z-10">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium text-text-muted border-b border-border w-10">#</th>
                {(current.rows[0] as unknown[]).map((cell, ci) => (
                  <th
                    key={ci}
                    className="px-3 py-1.5 text-left font-semibold text-text-primary border-b border-border whitespace-nowrap min-w-[80px]"
                  >
                    {formatCell(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {current.rows.slice(1).map((row, ri) => (
                <tr key={ri} className="hover:bg-surface-hover/50">
                  <td className="px-2 py-1 text-right text-text-muted border-b border-border/30">
                    {ri + 2}
                  </td>
                  {(row as unknown[]).map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-1 text-text-primary border-b border-border/30 whitespace-nowrap"
                    >
                      {formatCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="shrink-0 px-3 py-1 text-[10px] text-text-muted border-t border-border bg-surface/30">
        {current.rows.length > 0 ? `${current.rows.length - 1} rows` : "0 rows"}
        {sheets.length > 1 && ` · ${sheets.length} sheets`}
      </p>
    </div>
  );
}

/** Format an Excel cell value for display. */
function formatCell(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    // Format numbers with thousands separators, no more than 2 decimals
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (value instanceof Date) return value.toLocaleDateString();
  return String(value);
}

/** DOCX preview — convert .docx to HTML client-side via mammoth.
 *  Mammoth handles styling, lists, and tables out of the box. The
 *  rendered HTML is injected into a sandboxed div (no scripts in docx). */
function DocxPreview({ buffer }: { buffer: ArrayBuffer }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);

    // mammoth ships a CJS browser bundle without typings; import as
    // unknown and treat the entrypoint we use as a focused interface.
    import("mammoth/mammoth.browser" as string)
      .then((mod) => {
        if (cancelled) return undefined;
        const mammoth = (mod as { default?: unknown }).default ?? mod;
        const convert = (mammoth as {
          convertToHtml: (opts: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }>;
        }).convertToHtml;
        return convert({ arrayBuffer: buffer });
      })
      .then((result) => {
        if (cancelled || !result) return;
        setHtml(result.value);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to render document");
        }
      });

    return () => { cancelled = true; };
  }, [buffer]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 p-6">
        <FileType size={32} className="text-text-muted" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (html === null) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <Loader2 size={24} className="animate-spin" />
        <p className="text-sm">Rendering document...</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-surface">
      <div
        className="docx-rendered prose prose-sm max-w-none p-6 text-text-primary"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// ── Workspace browser ───────────────────────────────────────────────

interface WorkspaceEntry {
  name: string;
  path: string;
  absolutePath: string;
  isDir: boolean;
  size: number;
  mtime: number;
  ext: string;
}

interface WorkspaceListing {
  workspaceDir: string;
  currentPath: string;
  entries: WorkspaceEntry[];
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelativeTime(epochMs: number): string {
  if (!epochMs) return "";
  const diff = Date.now() - epochMs;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(epochMs).toLocaleDateString();
}

function WorkspaceBrowser({ agentId }: { agentId: string }) {
  const [path, setPath] = useState<string>("");
  // Visited-path stack for the Back button (like a browser history).
  const [history, setHistory] = useState<string[]>([]);
  const [listing, setListing] = useState<WorkspaceListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const { openArtifact } = useArtifact();

  // All navigation goes through navigateTo so Back always has the
  // previous location; goBack pops instead of pushing.
  const navigateTo = useCallback((next: string) => {
    setPath((current) => {
      if (next === current) return current;
      setHistory((h) => [...h, current]);
      return next;
    });
  }, []);
  const goBack = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      setPath(h[h.length - 1]);
      return h.slice(0, -1);
    });
  }, []);
  const goUp = useCallback(() => {
    setPath((current) => {
      if (!current) return current;
      const parent = current.split("/").slice(0, -1).join("/");
      setHistory((h) => [...h, current]);
      return parent;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const url = `/api/agents/${agentId}/workspace?path=${encodeURIComponent(path)}`;
    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || `Failed to list workspace (${res.status})`);
        }
        return res.json() as Promise<WorkspaceListing>;
      })
      .then((data) => {
        if (!cancelled) setListing(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load workspace");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [agentId, path, refreshKey]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      for (const f of files.slice(0, 5)) form.append("files", f);
      const res = await fetch(`/api/agents/${agentId}/files?path=${encodeURIComponent(path)}`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Upload failed (${res.status})`);
      }
      const result = (await res.json()) as { uploaded: string[]; failed: { name: string; error: string }[] };
      if (result.failed.length > 0) {
        setUploadError(`Failed to upload: ${result.failed.map((f) => f.name).join(", ")}`);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [agentId, path]);

  const segments = path ? path.split("/") : [];

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar: history/parent navigation, refresh, upload */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border bg-surface/30 shrink-0">
        <button
          onClick={goBack}
          disabled={history.length === 0}
          title="Back"
          className="p-1.5 rounded text-text-muted enabled:hover:text-text-primary enabled:hover:bg-surface-hover disabled:opacity-40"
        >
          <ArrowLeft size={14} />
        </button>
        <button
          onClick={goUp}
          disabled={!path}
          title="Up one level"
          className="p-1.5 rounded text-text-muted enabled:hover:text-text-primary enabled:hover:bg-surface-hover disabled:opacity-40"
        >
          <CornerLeftUp size={14} />
        </button>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          title="Refresh"
          className="p-1.5 rounded text-text-muted enabled:hover:text-text-primary enabled:hover:bg-surface-hover disabled:opacity-40"
        >
          <RotateCw size={14} className={loading ? "animate-spin" : undefined} />
        </button>
        <div className="flex-1" />
        <input
          ref={uploadInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void uploadFiles(files);
          }}
        />
        <button
          onClick={() => uploadInputRef.current?.click()}
          disabled={uploading}
          title="Upload files to this folder"
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>

      {/* Breadcrumbs — only shown once you've navigated into a subfolder. At the
          root there's nothing to trace back to (the "workspace" crumb would just
          be a dead "you are here" button), so we hide the whole bar. The root
          crumb reappears when deeper so you can click back up. */}
      {path && (
        <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-border bg-surface/30 text-xs overflow-x-auto shrink-0">
          <button
            onClick={() => navigateTo("")}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-surface-hover text-text-primary"
          >
            <FolderOpen size={12} />
            workspace
          </button>
          {segments.map((seg, i) => (
            <span key={i} className="flex items-center gap-0.5">
              <ChevronRight size={12} className="text-text-muted" />
              <button
                onClick={() => navigateTo(segments.slice(0, i + 1).join("/"))}
                className="px-1.5 py-0.5 rounded hover:bg-surface-hover text-text-primary"
              >
                {seg}
              </button>
            </span>
          ))}
        </div>
      )}

      {uploadError && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-red-500/5 text-xs text-red-600 dark:text-red-400 shrink-0">
          <span className="flex-1 truncate">{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="shrink-0 hover:text-text-primary">
            <X size={12} />
          </button>
        </div>
      )}

      {/* Listing — also a drop target for uploads into the current folder */}
      <div
        className={cn("flex-1 overflow-auto", isDragOver && "bg-accent/5 outline outline-2 -outline-offset-2 outline-accent/40")}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          void uploadFiles(Array.from(e.dataTransfer.files));
        }}
      >
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
            <Loader2 size={20} className="animate-spin" />
            <p className="text-xs">Loading workspace…</p>
          </div>
        )}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center h-full gap-2 p-6 text-center">
            <FolderTree size={28} className="text-text-muted" />
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}
        {!loading && !error && listing && listing.entries.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <p className="text-sm text-text-muted">Empty directory</p>
            <p className="text-xs text-text-muted/70">Drop files here or use Upload to add some</p>
          </div>
        )}
        {!loading && !error && listing && listing.entries.length > 0 && (
          <table className="min-w-full text-xs">
            <tbody>
              {listing.entries
                .slice()
                .sort((a, b) =>
                  a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1,
                )
                .map((entry) => {
                  const Icon = entry.isDir
                    ? Folder
                    : (FILE_ICONS[entry.ext] || File);
                  const canPreview = !entry.isDir && entry.ext;
                  return (
                    <tr
                      key={entry.path}
                      className="border-b border-border/30 hover:bg-surface-hover/50 cursor-pointer"
                      onClick={() => {
                        if (entry.isDir) {
                          navigateTo(entry.path);
                        } else if (canPreview) {
                          openArtifact(
                            {
                              type: "file",
                              content: "",
                              filePath: entry.absolutePath,
                              fileExt: entry.ext,
                              title: entry.name,
                            },
                            agentId,
                          );
                        }
                      }}
                    >
                      <td className="pl-3 pr-2 py-1.5 w-6">
                        <Icon size={14} className={entry.isDir ? "text-accent" : "text-text-muted"} />
                      </td>
                      <td className="px-1 py-1.5 text-text-primary truncate max-w-[260px]">
                        {entry.name}
                      </td>
                      <td className="px-2 py-1.5 text-right text-text-muted whitespace-nowrap">
                        {entry.isDir ? "" : formatSize(entry.size)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-text-muted whitespace-nowrap">
                        {formatRelativeTime(entry.mtime)}
                      </td>
                      {!entry.isDir && (
                        <td className="pr-3 pl-1 py-1.5 w-7">
                          <a
                            href={getFileProxyUrl(agentId, entry.absolutePath, true)}
                            onClick={(e) => e.stopPropagation()}
                            className="block p-1 rounded text-text-muted hover:text-text-primary hover:bg-surface-hover"
                            title="Download"
                          >
                            <Download size={12} />
                          </a>
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>

      {listing && (
        <div className="shrink-0 px-3 py-1 text-[10px] text-text-muted border-t border-border bg-surface/30 font-mono truncate">
          {listing.workspaceDir}{listing.currentPath ? `/${listing.currentPath}` : ""}
        </div>
      )}
    </div>
  );
}

/** Button to open the workspace browser — drop into chat header. */
export function WorkspaceTriggerButton({ agentId }: { agentId: string | null | undefined }) {
  const { openBrowser } = useArtifact();
  if (!agentId) return null;
  return (
    <ChatToolbarTooltip label="Open workspace files">
      <button
        onClick={() => openBrowser(agentId)}
        data-tour="workspace-trigger"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors"
        title="Open this agent's workspace files"
        aria-label="Open workspace files"
      >
        <FolderTree size={14} />
      </button>
    </ChatToolbarTooltip>
  );
}

// ── Artifact panel ──────────────────────────────────────────────────

const PANEL_WIDTH_KEY = "proclaw-artifact-panel-width";
const PANEL_MIN_WIDTH = 320;
// The panel may cover at most 80% of the chat area (chat column + panel).
const PANEL_MAX_WIDTH_RATIO = 0.8;
// The chat column stops shrinking at this width. Dragging the panel wider than
// that makes it slide OVER the chat instead of squeezing it (Kenneth,
// 2026-09-25): a chat squeezed to a sliver is unreadable.
const CHAT_MIN_WIDTH = 600;

function loadInitialWidth(): number {
  if (typeof window === "undefined") return 560;
  const stored = localStorage.getItem(PANEL_WIDTH_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  // Default: 45% of the viewport, clamped to [320, 600]
  return Math.min(600, Math.max(PANEL_MIN_WIDTH, Math.round(window.innerWidth * 0.45)));
}

export function ArtifactPanel() {
  const { isOpen, mode, artifact, agentId, cameFromBrowser, back, close } = useArtifact();
  const [width, setWidth] = useState<number>(loadInitialWidth);
  const [isDragging, setIsDragging] = useState(false);
  // The panel sits in a spacer inside the chat area's flex row. The spacer only
  // takes the room the chat can spare; the panel itself can be wider and then
  // overlaps the chat. The area's width drives both limits.
  const spacerRef = useRef<HTMLDivElement>(null);
  const [areaWidth, setAreaWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1200 : window.innerWidth,
  );
  const maxWidth = Math.max(PANEL_MIN_WIDTH, Math.floor(areaWidth * PANEL_MAX_WIDTH_RATIO));

  // Track the chat area's width (window resizes, the sidebar being dragged or
  // folded). Re-attached whenever the panel opens, since it unmounts when shut.
  useEffect(() => {
    if (!isOpen) return;
    const area = spacerRef.current?.parentElement;
    if (!area) return;
    const measure = () => setAreaWidth(area.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    return () => observer.disconnect();
  }, [isOpen, mode]);

  // Keep the panel within its limit when the area gets narrower.
  useEffect(() => {
    setWidth((w) => Math.min(Math.max(PANEL_MIN_WIDTH, w), maxWidth));
  }, [maxWidth]);

  // Drag handler — listens on window so the user can move fast without
  // losing the pointer. Disables text selection during drag.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: PointerEvent) => {
      // Panel is pinned to the right edge of the chat area; its width grows as
      // the pointer moves left, so width = (area's right edge) - (pointer x)
      const right =
        spacerRef.current?.parentElement?.getBoundingClientRect().right ?? window.innerWidth;
      const next = Math.min(maxWidth, Math.max(PANEL_MIN_WIDTH, right - e.clientX));
      setWidth(next);
    };
    const onUp = () => {
      setIsDragging(false);
      try {
        localStorage.setItem(PANEL_WIDTH_KEY, String(width));
      } catch { /* quota / privacy mode */ }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    // Prevent text selection while dragging
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, width, maxWidth]);

  // Let a guided walkthrough close the panel when it moves on (so the panel
  // doesn't linger over the next step, e.g. the Scout bubble).
  useEffect(() => {
    const onClose = () => close();
    window.addEventListener("proclaw:close-artifact", onClose);
    return () => window.removeEventListener("proclaw:close-artifact", onClose);
  }, [close]);

  // Ctrl/Cmd+A selects only the panel's content once the user has clicked in
  // it. Panel content is not focusable, so the browser's own select-all used
  // to take the whole page: sidebar, chat and panel together. Typing fields
  // keep their normal select-all, and iframes (HTML and PDF previews) already
  // scope it themselves because the click moves focus into them.
  const contentRef = useRef<HTMLDivElement>(null);
  const lastClickInPanel = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      lastClickInPanel.current = !!contentRef.current?.contains(e.target as Node);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "a") return;
      if (!lastClickInPanel.current || !contentRef.current) return;
      const active = document.activeElement as HTMLElement | null;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)) return;
      e.preventDefault();
      window.getSelection()?.selectAllChildren(contentRef.current);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;
  // Artifact mode needs a payload; browser mode needs an agent ID.
  if (mode === "artifact" && !artifact) return null;
  if (mode === "browser" && !agentId) return null;

  const downloadUrl =
    mode === "artifact" && artifact?.type === "file" && artifact.filePath && agentId
      ? getFileProxyUrl(agentId, artifact.filePath, true)
      : null;

  // Room the chat can give up before reaching its minimum width. Anything the
  // panel needs beyond that, it takes by overlapping the chat.
  const reserved = Math.min(width, Math.max(PANEL_MIN_WIDTH, areaWidth - CHAT_MIN_WIDTH));
  const overlapsChat = width > reserved;

  return (
    <div ref={spacerRef} className="relative h-full shrink-0" style={{ width: `${reserved}px` }}>
    <div
      data-tour="workspace-panel"
      className={cn(
        "absolute inset-y-0 right-0 z-20 border-l border-border bg-background flex flex-col animate-fade-in max-sm:w-full max-sm:border-l-0",
        // A shadow on the left edge once it sits over the chat, so the panel
        // reads as a layer on top rather than a cut-off chat.
        overlapsChat && "shadow-[-12px_0_24px_-12px_rgba(0,0,0,0.45)]",
      )}
      style={{ width: `${width}px` }}
    >
      {/* Drag handle on the left edge — hidden on phones (no cursor) */}
      <div
        onPointerDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDoubleClick={() => {
          // Double-click to reset to default width
          const reset = Math.min(600, Math.max(PANEL_MIN_WIDTH, Math.round(areaWidth * 0.45)));
          setWidth(reset);
          try { localStorage.setItem(PANEL_WIDTH_KEY, String(reset)); } catch { /* noop */ }
        }}
        className={cn(
          "hidden sm:block absolute top-0 bottom-0 -left-[3px] w-[6px] cursor-col-resize z-10",
          "hover:bg-accent/30 transition-colors",
          isDragging && "bg-accent/50",
        )}
        title="Drag to resize · double-click to reset"
      />

      {/* Header — min-h-14 + py-2 mirrors the chat column's agent/channel
          header height so the two panels' top rows line up (symmetry). */}
      <div className="flex min-h-14 items-center gap-2 px-4 py-2 border-b border-border bg-surface shrink-0">
        {mode === "browser" ? (
          <>
            <FolderTree size={14} />
            <span className="text-sm font-medium text-text-primary flex-1 truncate">
              Workspace files
            </span>
          </>
        ) : artifact ? (
          <>
            <ArtifactIcon type={artifact.type} fileExt={artifact.fileExt} />
            <span className="text-sm font-medium text-text-primary flex-1 truncate">
              {artifact.title || "Artifact"}
            </span>
            {artifact.language && (
              <span className="text-[10px] font-mono text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">
                {artifact.language}
              </span>
            )}
            {artifact.fileExt && (
              <span className="text-[10px] font-mono text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">
                .{artifact.fileExt}
              </span>
            )}
            {downloadUrl && (
              <a
                href={downloadUrl}
                className="p-1 rounded hover:bg-surface-hover transition-colors text-text-muted hover:text-text-primary"
                title="Download file"
              >
                <Download size={14} />
              </a>
            )}
          </>
        ) : null}
        {/* When a document was opened from the files browser, this control goes
            BACK to the files list rather than closing the whole panel (from the
            list itself, or a standalone artifact, it closes as before). */}
        {mode === "artifact" && cameFromBrowser ? (
          <button
            onClick={back}
            className="p-1 rounded hover:bg-surface-hover transition-colors text-text-muted hover:text-text-primary"
            title="Back to files"
            aria-label="Back to workspace files"
          >
            <ArrowLeft size={16} />
          </button>
        ) : (
          <button
            onClick={close}
            className="p-1 rounded hover:bg-surface-hover transition-colors text-text-muted hover:text-text-primary"
            title="Close panel"
            aria-label="Close panel"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-hidden">
        {mode === "browser" && agentId && <WorkspaceBrowser agentId={agentId} />}
        {mode === "artifact" && artifact?.type === "html" && <HtmlPreview content={artifact.content} />}
        {mode === "artifact" && artifact?.type === "svg" && <SvgPreview content={artifact.content} />}
        {mode === "artifact" && artifact?.type === "code" && (
          <CodePreview content={artifact.content} language={artifact.language} />
        )}
        {mode === "artifact" && artifact?.type === "file" && artifact.filePath && artifact.fileExt && agentId && (
          <FilePreview agentId={agentId} filePath={artifact.filePath} fileExt={artifact.fileExt} />
        )}
      </div>
    </div>
    </div>
  );
}

// ── Inline artifact button (used in message bubbles) ────────────────

export function ArtifactButton({ artifact, agentId }: { artifact: Artifact; agentId?: string }) {
  const { openArtifact } = useArtifact();

  return (
    <button
      onClick={() => openArtifact(artifact, agentId)}
      className={cn(
        "inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium",
        "bg-accent/10 text-accent hover:bg-accent/20 transition-colors",
      )}
    >
      <ArtifactIcon type={artifact.type} fileExt={artifact.fileExt} />
      {artifact.type === "file" ? `Open ${artifact.title || "file"}` : `View ${artifact.title || "artifact"}`}
    </button>
  );
}

// ── Auto-open hook ──────────────────────────────────────────────────
//
// Drop into MessageBubble: if the assistant message is fresh (just
// arrived, not historic) and produced exactly one artifact, open it
// in the canvas automatically. Keeps a per-message ref so a re-render
// never re-opens an artifact the user already closed.
//
// Multiple artifacts → render chips only; the user picks.

const AUTO_OPEN_FRESHNESS_MS = 30_000;
const autoOpenedMessages = new Set<string>();

export function useArtifactAutoOpen(opts: {
  messageId: string;
  createdAt: string;
  artifacts: Artifact[];
  agentId?: string;
}) {
  const { openArtifact, isOpen } = useArtifact();

  useEffect(() => {
    if (autoOpenedMessages.has(opts.messageId)) return;
    const created = new Date(opts.createdAt).getTime();
    if (Number.isNaN(created) || Date.now() - created > AUTO_OPEN_FRESHNESS_MS) return;
    if (opts.artifacts.length !== 1) return;
    if (isOpen) return; // don't yank focus from a panel the user is using
    autoOpenedMessages.add(opts.messageId);
    openArtifact(opts.artifacts[0], opts.agentId);
    // openArtifact / isOpen are stable enough that we only re-run on id/artifact change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.messageId, opts.artifacts.length]);
}
